import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './CVSettings.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore } from 'src/content/icons';
import { ConnectedVehicleRec } from 'src/models';
import { Dialog, ScrollBar, Link } from '@framework-controls/core';
import { strings } from 'src/localization';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
}

const CVSettings = (props: IProps) => {
    const [infoOpen, setInfoOpen] = useState(false);
    const toggleInfo = useCallback(() => {
        setInfoOpen(!infoOpen);
    }, [infoOpen]);

    let deviceName: ReactElement | null = null;
    if (props.connectedVehicle.deviceName) {
        let deviceLink: ReactElement =
            <div className={css.infoData}>{props.connectedVehicle.deviceName}</div>;

        if (props.connectedVehicle.deviceUrl) {
            deviceLink =
                <div>
                    <Link
                        id='DeviceLink'
                        url={props.connectedVehicle.deviceUrl}
                        className={css.url}>
                        <div>{props.connectedVehicle.deviceName}</div>
                    </Link>
                </div>;
        }
        deviceName =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DeviceName}
                </div>
                {deviceLink}
            </span>;
    }

    let capabilities: ReactElement | null = null;
    if (props.connectedVehicle.tele) {
        let teleValue = props.connectedVehicle.tele;
        if (props.connectedVehicle.tele === 'Y') {
            teleValue = strings.Yes;
        }
        if (props.connectedVehicle.tele === 'N') {
            teleValue = strings.No;
        }
        capabilities =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TeleserviceCapabilities}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let remote: ReactElement | null = null;
    if (props.connectedVehicle.remTeleCalls) {
        let teleValue = props.connectedVehicle.remTeleCalls;
        if (props.connectedVehicle.remTeleCalls === 'Y') {
            teleValue = strings.Yes;
        }
        if (props.connectedVehicle.remTeleCalls === 'N') {
            teleValue = strings.No;
        }
        remote =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RemoteTeleserviceCalls}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let contract: ReactElement | null = null;
    if (props.connectedVehicle.teleContract) {
        let teleValue = props.connectedVehicle.teleContract;
        if (props.connectedVehicle.teleContract === 'Y') {
            teleValue = strings.Yes;
        }
        if (props.connectedVehicle.teleContract === 'N') {
            teleValue = strings.No;
        }
        contract =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.TeleserviceContract}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let ssc: ReactElement | null = null;
    if (props.connectedVehicle.sscFlag) {
        let teleValue = props.connectedVehicle.sscFlag;
        if (props.connectedVehicle.sscFlag === 'Y') {
            teleValue = strings.Yes;
        }
        if (props.connectedVehicle.sscFlag === 'N') {
            teleValue = strings.No;
        }
        ssc =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ServiceSafetyCampaignSSCIndicator}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let damaged: ReactElement | null = null;
    if (props.connectedVehicle.damagedFlag) {
        let teleValue = props.connectedVehicle.damagedFlag;
        if (props.connectedVehicle.damagedFlag === 'Y') {
            teleValue = strings.Yes;
        }
        if (props.connectedVehicle.sscFlag === 'N') {
            teleValue = strings.No;
        }
        damaged =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Damaged}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let lastUpdate: ReactElement | null = null;
    if (props.connectedVehicle.vehUpdatedTstamp) {
        lastUpdate =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VehicleLastUpdated}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.vehUpdatedTstamp}
                </div>
            </span>;
    }

    let checkCode: ReactElement | null = null;
    if (props.connectedVehicle.vehCheckCode) {
        checkCode =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VehicleCheckCode}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.vehCheckCode}
                </div>
            </span>;
    }

    let dealer: ReactElement | null = null;
    if (props.connectedVehicle.dlrCategory) {
        dealer =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DealerCategory}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.dlrCategory}
                </div>
            </span>;
    }

    let supplyChain: ReactElement | null = null;
    if (props.connectedVehicle.vehCategory) {
        supplyChain =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.SupplyChainCategory}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.vehCategory}
                </div>
            </span>;
    }

    let auth: ReactElement | null = null;
    if (props.connectedVehicle.auth) {
        auth =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Authorization}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.auth}
                </div>
            </span>;
    }

    let location: ReactElement | null = null;
    if (props.connectedVehicle.settingsLocData) {
        let dataValue = props.connectedVehicle.settingsLocData;
        if (props.connectedVehicle.settingsLocData === 'Y') {
            dataValue = strings.On;
        }
        if (props.connectedVehicle.settingsLocData === 'N') {
            dataValue = strings.Off;
        }
        let dataTstamp: ReactElement | null = null;
        if (props.connectedVehicle.settingsTstamp) {
            dataTstamp =
                <p className={css.statusLit}>{strings.DataAsOf + ' ' + props.connectedVehicle.settingsTstamp}</p>;
        }
        location =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.LocationData}
                </div>
                <div className={css.infoData}>
                    {dataValue}
                </div>
                {dataTstamp}
            </span>;
    }

    let connectivity: ReactElement | null = null;
    if (props.connectedVehicle.settingsConnect) {
        let dataValue = props.connectedVehicle.settingsConnect;
        if (props.connectedVehicle.settingsConnect === 'Y') {
            dataValue = strings.On;
        }
        if (props.connectedVehicle.settingsConnect === 'N') {
            dataValue = strings.Off;
        }
        let dataTstamp: ReactElement | null = null;
        if (props.connectedVehicle.settingsTstamp) {
            dataTstamp =
                <p className={css.statusLit}>{strings.DataAsOf + ' ' + props.connectedVehicle.settingsTstamp}</p>;
        }
        connectivity =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VehicleConnectivity}
                </div>
                <div className={css.infoData}>
                    {dataValue}
                </div>
                {dataTstamp}
            </span>;
    }

    let vehicleData: ReactElement | null = null;
    if (props.connectedVehicle.settingsData) {
        let dataValue = props.connectedVehicle.settingsData;
        if (props.connectedVehicle.settingsData === 'Y') {
            dataValue = strings.On;
        }
        if (props.connectedVehicle.settingsData === 'N') {
            dataValue = strings.Off;
        }
        let dataTstamp: ReactElement | null = null;
        if (props.connectedVehicle.settingsTstamp) {
            dataTstamp =
                <p className={css.statusLit}>{strings.DataAsOf + ' ' + props.connectedVehicle.settingsTstamp}</p>;
        }
        vehicleData =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VehicleData}
                </div>
                <div className={css.infoData}>
                    {dataValue}
                </div>
                {dataTstamp}
            </span>;
    }

    let safety: ReactElement | null = null;
    if (props.connectedVehicle.safety) {
        let teleValue = props.connectedVehicle.safety;
        if (props.connectedVehicle.safety === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.safety === 'N') {
            teleValue = strings.NotActive;
        }
        safety =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Safety}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let serviceConnect: ReactElement | null = null;
    if (props.connectedVehicle.svcConnect) {
        let teleValue = props.connectedVehicle.svcConnect;
        if (props.connectedVehicle.svcConnect === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.svcConnect === 'N') {
            teleValue = strings.NotActive;
        }
        serviceConnect =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ServiceConnect}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let remoteActive: ReactElement | null = null;
    if (props.connectedVehicle.remoteActive) {
        let teleValue = props.connectedVehicle.remoteActive;
        if (props.connectedVehicle.remoteActive === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.remoteActive === 'N') {
            teleValue = strings.NotActive;
        }
        remoteActive =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Remote}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let digiKeyRemote: ReactElement | null = null;
    if (props.connectedVehicle.digitalKeyRemote) {
        let teleValue = props.connectedVehicle.digitalKeyRemote;
        if (props.connectedVehicle.digitalKeyRemote === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.digitalKeyRemote === 'N') {
            teleValue = strings.NotActive;
        }
        digiKeyRemote =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DigitalKeyRemote}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let destAsst: ReactElement | null = null;
    if (props.connectedVehicle.destinationAssist) {
        let teleValue = props.connectedVehicle.destinationAssist;
        if (props.connectedVehicle.destinationAssist === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.destinationAssist === 'N') {
            teleValue = strings.NotActive;
        }
        destAsst =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.DestinationAssist}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let nav: ReactElement | null = null;
    if (props.connectedVehicle.navigation) {
        let teleValue = props.connectedVehicle.navigation;
        if (props.connectedVehicle.navigation === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.navigation === 'N') {
            teleValue = strings.NotActive;
        }
        nav =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Navigation}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let virtualAsst: ReactElement | null = null;
    if (props.connectedVehicle.virtualAssistant) {
        let teleValue = props.connectedVehicle.virtualAssistant;
        if (props.connectedVehicle.virtualAssistant === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.virtualAssistant === 'N') {
            teleValue = strings.NotActive;
        }
        virtualAsst =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.VirtualAssist}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let intgStreaming: ReactElement | null = null;
    if (props.connectedVehicle.integratedStreaming) {
        let teleValue = props.connectedVehicle.integratedStreaming;
        if (props.connectedVehicle.integratedStreaming === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.integratedStreaming === 'N') {
            teleValue = strings.NotActive;
        }
        intgStreaming =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.IntegratedStreaming}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    let wifi: ReactElement | null = null;
    if (props.connectedVehicle.wifi) {
        let teleValue = props.connectedVehicle.wifi;
        if (props.connectedVehicle.wifi === 'Y') {
            teleValue = strings.Active;
        }
        if (props.connectedVehicle.wifi === 'N') {
            teleValue = strings.NotActive;
        }
        wifi =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Wifi}
                </div>
                <div className={css.infoData}>
                    {teleValue}
                </div>
            </span>;
    }

    return (
        <div className={css.block}>
            <span className={css.header}>
                <div className={css.headerLit}>{strings.ViewConnectedVehicleSettings}</div>
                <IconSeeMore className={css.icon} onClick={() => toggleInfo()} />
            </span>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={infoOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.ConnectedVehicleSettings.toUpperCase()}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleInfo()}
                onBackDropClick={() => toggleInfo()}
                width={'full'}
            >
                <ScrollBar scrollVertical>
                    <div className={css.infoRow}>
                        {deviceName}
                        {capabilities}
                        {remote}
                        {contract}
                        {ssc}
                        {damaged}
                        {lastUpdate}
                        {checkCode}
                        {dealer}
                        {supplyChain}
                        {auth}
                        {location}
                        {connectivity}
                        {vehicleData}
                        {safety}
                        {serviceConnect}
                        {remoteActive}
                        {digiKeyRemote}
                        {destAsst}
                        {nav}
                        {virtualAsst}
                        {intgStreaming}
                        {wifi}
                    </div>
                </ScrollBar>
            </Dialog>
        </div>
    );
};

export default CVSettings;